/**
 * Builder utilities for action-driven block composition.
 *
 * These are pure functions that produce typed block descriptor objects.
 * Used inside builder action classes (BUILD_*) to construct render trees.
 * No React, no JSX — just data.
 */

import { createElement, memo, useSyncExternalStore, type ComponentType } from 'react';
import { getControlRendererRef } from '../core/controlRendererRef';
import type {
  ViewBlock,
  TableBlock,
  TabBlock,
  TabEntry,
  StackBlock,
  FormBlock,
  FormBlockMode,
  WizardBlock,
  WizardStep,
  AccordionBlock,
  AccordionSection,
  ButtonBlock,
  ActionBlock,
  SlotBlock,
  AnyBlock,
} from '../types/blocks';

export interface TabConfig {
  id: string;
  label: string;
  block: AnyBlock;
  /** When true, the tab unmounts when hidden and remounts fresh on every visit. */
  remountOnSwitch?: boolean;
}

export interface ColumnConfig {
  key: string;
  label: string;
  [k: string]: unknown;
}

let blockCounter = 0;
function autoId(prefix: string): string {
  blockCounter += 1;
  return `builder://${prefix}/${blockCounter}`;
}

export interface ViewBlockConfig {
  /** Pre-fetched data from a builder action — hydrates instantly, skips initial fetch. */
  data?: Record<string, unknown>;
  /**
   * Action ID for self-fetch. The renderer auto-constructs `{ id: params.id }` params.
   * Prefer declaring this in `domain.json` `modes.view.fetchActionId` instead of here.
   * When both `data` and `fetchActionId` are present: data hydrates first paint,
   * fetchActionId subscribes for cache invalidation and re-fetch.
   */
  fetchActionId?: string;
  presentation?: 'default' | 'surface';
  semanticId?: string;
  /** @deprecated Use `fetchActionId` string instead. Kept for wizard context backward compat. */
  dataSource?: ViewBlock['dataSource'];
  skeletonOnFetch?: boolean;
}

/**
 * Create a view block.
 *
 * - With `data`: snapshot hydrate — instant first paint.
 * - With `fetchActionId`: self-fetch via ActionRegistry; responds to cache invalidation.
 * - With both: `data` hydrates instantly, `fetchActionId` subscribes for re-fetch after edits.
 * - With neither: uses domain.json `modes.view.fetchActionId` if declared.
 */
export function viewBlock(
  schemaId: string,
  config?: ViewBlockConfig,
): ViewBlock {
  return {
    $type: 'view',
    $id: schemaId,
    semanticId: config?.semanticId,
    presentation: config?.presentation,
    ...(config?.fetchActionId != null ? { fetchActionId: config.fetchActionId } : {}),
    ...(config?.dataSource != null ? { dataSource: config.dataSource } : {}),
    ...(config?.skeletonOnFetch != null ? { skeletonOnFetch: config.skeletonOnFetch } : {}),
    ...(config?.data !== undefined ? { data: config.data } : {}),
  };
}

/**
 * Create a table block by schema reference.
 * The table renderer loads the schema and handles data fetching, pagination, etc.
 * Optionally pass pre-fetched `data` + `columns` for static rendering.
 */
export function tableBlock(
  schemaId: string,
  opts?: {
    namespace?: string;
    semanticId?: string;
    title?: string;
    data?: unknown[];
    columns?: ColumnConfig[];
    context?: Record<string, unknown>;
  },
): TableBlock {
  return {
    $type: 'table',
    $id: schemaId,
    schema: schemaId,
    namespace: opts?.namespace,
    semanticId: opts?.semanticId,
    title: opts?.title,
    data: opts?.data,
    columns: opts?.columns,
    context: opts?.context,
  };
}

/**
 * Create a tab container block.
 */
export function tabBlock(
  tabs: TabConfig[],
  opts?: { $id?: string; tabsAlign?: 'start' | 'center'; semanticId?: string; defaultTab?: string },
): TabBlock {
  return {
    $type: 'tab',
    $id: opts?.$id ?? autoId('tab'),
    semanticId: opts?.semanticId,
    tabsAlign: opts?.tabsAlign ?? 'center',
    tabs: tabs as TabEntry[],
    ...(opts?.defaultTab != null && { defaultTab: opts.defaultTab }),
  };
}

/**
 * Create a stack block (vertical or horizontal list of child blocks).
 * `fetchActionId` is inherited by all child view blocks that don't declare their own.
 */
export function stackBlock(
  blocks: AnyBlock[],
  opts?: {
    gap?: string;
    semanticId?: string;
    fetchActionId?: string;
    direction?: 'row' | 'column';
    align?: 'start' | 'end' | 'between' | 'center';
    wrap?: boolean;
    sticky?: boolean;
    divider?: boolean;
    effects?: import('../types/blocks').StackBlock['effects'];
  },
): StackBlock {
  return {
    $type: 'stack',
    $id: autoId('stack'),
    semanticId: opts?.semanticId,
    blocks,
    gap: opts?.gap,
    ...(opts?.direction != null ? { direction: opts.direction } : {}),
    ...(opts?.align != null ? { align: opts.align } : {}),
    ...(opts?.wrap === true ? { wrap: true } : {}),
    ...(opts?.sticky === true ? { sticky: true } : {}),
    ...(opts?.divider === true ? { divider: true } : {}),
    ...(opts?.fetchActionId != null ? { fetchActionId: opts.fetchActionId } : {}),
    ...(opts?.effects != null ? { effects: opts.effects } : {}),
  };
}

interface FormBlockOpts {
  entityId?: string;
  data?: Record<string, unknown>;
  semanticId?: string;
  hideTitle?: boolean;
  hideSubmitBar?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * Accepted mode values for formBlock(). Superset of FormBlockMode that also
 * includes modal-only modes from ComposeModalMode ('create', 'cancel-only', 'approver').
 * Legacy `'add'` is treated as undefined (auto-resolve) — prefer `'create'`.
 *
 * Only FormBlockMode values ('create' | 'edit' | 'view' | 'upsert') are preserved
 * on the output block. Modal-only modes are treated as undefined (auto-resolve
 * based on entityId presence).
 *
 * This allows builder actions to pass `meta?.mode` directly without conversion.
 */
type FormBlockModeInput = FormBlockMode | 'add' | 'cancel-only' | 'approver';

/**
 * Create a form block by schema reference (e.g. "wealthlane://forms/participant").
 *
 * Mode is optional — when omitted, create vs edit is auto-resolved from the
 * entity ID at render time (FormBlockRenderer). Pass `'view'` explicitly for
 * read-only rendering.
 *
 * ```ts
 * formBlock('wealthlane://forms/participant', { entityId })          // auto
 * formBlock('wealthlane://forms/participant')                         // auto (create)
 * formBlock('wealthlane://forms/participant', 'view', { entityId })   // explicit view
 * ```
 */
export function formBlock(schemaId: string, opts?: FormBlockOpts): FormBlock;
export function formBlock(schemaId: string, mode: FormBlockModeInput | undefined, opts?: FormBlockOpts): FormBlock;
export function formBlock(
  schemaId: string,
  modeOrOpts?: FormBlockModeInput | undefined | FormBlockOpts,
  opts?: FormBlockOpts,
): FormBlock {
  const isMode = typeof modeOrOpts === 'string';
  const rawMode = isMode ? modeOrOpts : undefined;
  const resolvedOpts = isMode ? opts : modeOrOpts;
  const mode: FormBlockMode | undefined =
    rawMode === 'view' || rawMode === 'edit' || rawMode === 'create' || rawMode === 'upsert'
      ? rawMode
      : undefined;
  return {
    $type: 'form',
    $id: schemaId,
    semanticId: resolvedOpts?.semanticId,
    schema: {} as FormBlock['schema'],
    ...(mode != null && { mode }),
    entityId: resolvedOpts?.entityId,
    data: resolvedOpts?.data,
    ...(resolvedOpts?.hideTitle && { hideTitle: true }),
    ...(resolvedOpts?.hideSubmitBar && { hideSubmitBar: true }),
    ...(resolvedOpts?.onDirtyChange && { onDirtyChange: resolvedOpts.onDirtyChange }),
  };
}

/**
 * Create a wizard block with steps.
 */
export function wizardBlock(
  steps: WizardStep[],
  opts?: { semanticId?: string; onComplete?: { actionId: string } },
): WizardBlock {
  return {
    $type: 'wizard',
    $id: autoId('wizard'),
    semanticId: opts?.semanticId,
    steps,
    onComplete: opts?.onComplete,
  };
}

export interface AccordionConfig {
  id: string;
  label: string;
  block: AnyBlock;
}

/**
 * Create an accordion container block (collapsible sections).
 */
export function accordionBlock(
  sections: AccordionConfig[],
  opts?: { accordionType?: 'single' | 'multiple'; grouped?: boolean; semanticId?: string },
): AccordionBlock {
  return {
    $type: 'accordion',
    $id: autoId('accordion'),
    semanticId: opts?.semanticId,
    accordionType: opts?.accordionType ?? 'single',
    grouped: opts?.grouped,
    sections: sections as AccordionSection[],
  };
}

// ─── Action Block builder ─────────────────────────────────────────────────────

export interface ActionBlockConfig {
  semanticId?: string;
  /** Per-block config merged into `ctx.meta` when the nested builder executes. */
  blockMeta?: Record<string, unknown>;
}

/**
 * Create an action block — delegates rendering to another builder action.
 *
 * Use this to compose existing `BUILD_*` actions as child blocks inside a
 * parent builder, passing per-block configuration through `blockMeta`.
 *
 * ```ts
 * // Embed the participant form in view mode inside a view builder:
 * actionBlock('BUILD_PARTICIPANT_FORM', { blockMeta: { formMode: 'view', hideTitle: true } })
 *
 * // Nest a builder action with no extra config:
 * actionBlock('BUILD_AWARD_DOCUMENTS')
 * ```
 */
export function actionBlock(
  actionId: string,
  config?: ActionBlockConfig,
): ActionBlock {
  return {
    $type: 'action',
    $id: `action://${actionId}`,
    actionId,
    semanticId: config?.semanticId,
    ...(config?.blockMeta != null ? { blockMeta: config.blockMeta } : {}),
  };
}

/**
 * Convenience layout: surface summary view + centered tab strip.
 * Used by BUILD_* actions for entity detail pages.
 */
export function summaryAndTabs(
  summarySchema: string,
  tabs: TabConfig[],
  opts?: {
    fetchActionId?: string;
    data?: Record<string, unknown>;
    summaryPresentation?: ViewBlockConfig['presentation'];
  },
): StackBlock {
  const summary = viewBlock(summarySchema, {
    data: opts?.data,
    presentation: opts?.summaryPresentation ?? 'surface',
  });
  const tabSection = tabBlock(tabs, { tabsAlign: 'center' });
  return stackBlock([summary, tabSection], { fetchActionId: opts?.fetchActionId });
}

/**
 * Convenience layout: summary at `{schemaPrefix}/summary` + tab views at `{schemaPrefix}/{schema}`.
 */
export function detailViewLayout(
  schemaPrefix: string,
  tabDefs: Array<{ id: string; label: string; schema: string }>,
  opts?: { fetchActionId?: string; data?: Record<string, unknown> },
): StackBlock {
  const summaryView = viewBlock(`${schemaPrefix}/summary`, {
    data: opts?.data,
    presentation: 'surface',
  });
  const tabEntries = tabDefs.map((def) => ({
    id: def.id,
    label: def.label,
    block: viewBlock(`${schemaPrefix}/${def.schema}`, { data: opts?.data }),
  }));
  const tabSection = tabBlock(tabEntries, { tabsAlign: 'center' });
  return stackBlock([summaryView, tabSection], { fetchActionId: opts?.fetchActionId });
}

// ─── Button Block builders ────────────────────────────────────────────────────

export interface ButtonBlockConfig {
  variant?: ButtonBlock['variant'];
  size?: ButtonBlock['size'];
  icon?: string;
  trailingIcon?: string;
  semanticId?: string;
  eventPayload?: Record<string, unknown>;
  /** ActionRegistry action to execute on click (independently of the event). */
  actionId?: string;
  disabled?: boolean;
}

/**
 * Create a single button block — a pure event emitter.
 *
 * The button emits a named `CUSTOM` event into the BlockEvents bus on click.
 * It has no knowledge of forms, views, or tables.
 *
 * @param label - Button display text
 * @param event - Event name to emit (e.g. 'SAVE', 'PREVIEW_VESTING', 'EXPORT_CSV')
 * @param config - Optional visual and payload configuration
 */
export function buttonBlock(
  label: string,
  event: string,
  config?: ButtonBlockConfig,
): ButtonBlock {
  return {
    $type: 'button',
    $id: autoId('button'),
    label,
    event,
    semanticId: config?.semanticId,
    ...(config?.variant != null ? { variant: config.variant } : {}),
    ...(config?.size != null ? { size: config.size } : {}),
    ...(config?.icon != null ? { icon: config.icon } : {}),
    ...(config?.trailingIcon != null ? { trailingIcon: config.trailingIcon } : {}),
    ...(config?.eventPayload != null ? { eventPayload: config.eventPayload } : {}),
    ...(config?.actionId != null ? { actionId: config.actionId } : {}),
    ...(config?.disabled === true ? { disabled: true } : {}),
  };
}

/**
 * Create a horizontal button bar — a row-direction stack containing button blocks.
 *
 * Convenience wrapper: `buttonBar([...]) === stackBlock([...], { direction: 'row', align: 'end' })`.
 *
 * @param buttons - Array of ButtonBlock instances (from `buttonBlock()`)
 * @param opts - Optional layout configuration (align, sticky, divider, gap)
 */
export function buttonBar(
  buttons: ButtonBlock[],
  opts?: {
    align?: 'start' | 'end' | 'between' | 'center';
    sticky?: boolean;
    divider?: boolean;
    gap?: string;
    semanticId?: string;
  },
): StackBlock {
  return stackBlock(buttons, {
    direction: 'row',
    align: opts?.align ?? 'end',
    gap: opts?.gap ?? '12px',
    sticky: opts?.sticky,
    divider: opts?.divider,
    semanticId: opts?.semanticId,
  });
}

let _slotCounter = 0;

/**
 * Create a slot block — embeds a bespoke React component inline in the compose tree.
 *
 * Use this when an existing component (with its own hooks, props, and internal state)
 * needs to appear as a tab content or stack child inside a builder-action tree, without
 * being fully migrated to a named schema.
 *
 * The `render` factory is a closure — capture any props the component needs at call time:
 *
 * ```ts
 * slotBlock(() => <ReleaseSelectedItemsTable releaseId={id} ... />)
 * ```
 *
 * SlotBlock is NOT serialisable. Only return it from builder actions at runtime.
 */
export function slotBlock(
  render: (shared: import('../types/blocks').BlockRendererSharedProps) => JSX.Element,
  opts?: { semanticId?: string; wire?: import('../types/blocks').SlotBlock['wire'] },
): SlotBlock {
  return {
    $type: 'slot',
    $id: `slot:${++_slotCounter}`,
    semanticId: opts?.semanticId,
    wire: opts?.wire,
    render,
  };
}

/**
 * Create a slot block from a React component reference + optional props.
 * Unlike `slotBlock`, this does NOT require JSX — works in plain `.ts` files.
 *
 * ```ts
 * componentBlock(ReleaseSelectedItemsTable, { semanticId: 'selectItems' })
 * componentBlock(MyComponent, { semanticId: 'foo', props: { releaseId: '123' } })
 * ```
 */
export function componentBlock<P extends Record<string, unknown> = Record<string, never>>(
  Component: ComponentType<P>,
  opts?: { semanticId?: string; props?: P },
): SlotBlock {
  return slotBlock(
    (_shared) => createElement(Component, (opts?.props ?? {}) as P),
    { semanticId: opts?.semanticId },
  );
}

/**
 * Reactive slot that subscribes to an external store and calls a
 * pure block-factory whenever the store value changes.
 *
 * The factory receives the current snapshot and returns a block tree
 * (rendered via `ControlRenderer`) or `null` to render nothing.
 *
 * This keeps all decision logic in plain `.ts` — no hooks, no JSX needed
 * in the consumer. The reactive bridge is handled internally.
 *
 * ```ts
 * reactiveSlotBlock(
 *   subscribeReleaseSession,
 *   getReleaseSessionContext,
 *   (session) => {
 *     if (!session.submitted) return null;
 *     return tabBlock([ ... ]);
 *   },
 *   { semanticId: 'releaseFormTabsGate' },
 * )
 * ```
 */
/**
 * Internal wrapper component for `reactiveSlotBlock`.
 * Subscribes to an external store, calls the factory, renders the result.
 */
const ReactiveSlotInner = memo(function ReactiveSlotInner<T>({
  subscribe,
  getSnapshot,
  factory,
  wrapperStyle,
  wrapperClassName,
}: {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => T;
  factory: (snapshot: T) => AnyBlock | null;
  wrapperStyle?: Record<string, unknown>;
  wrapperClassName?: string;
}) {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  const block = factory(snapshot);
  if (block == null) return null;

  const Renderer = getControlRendererRef();
  const el = createElement(Renderer, { block });
  if (wrapperStyle || wrapperClassName) {
    return createElement('div', { style: wrapperStyle, className: wrapperClassName }, el);
  }
  return el;
}) as <T>(props: {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => T;
  factory: (snapshot: T) => AnyBlock | null;
  wrapperStyle?: Record<string, unknown>;
  wrapperClassName?: string;
}) => JSX.Element | null;

/**
 * Responsive horizontal inset for reactive slot wrappers (form tabs, gated panels).
 * Applied via `reactiveSlotBlock({ wrapperInset: true })`.
 */
export const COMPOSE_REACTIVE_SLOT_INSET_CLASS = 'compose-reactive-slot-inset';

function resolveReactiveSlotWrapperClassName(opts?: {
  wrapperClassName?: string;
  wrapperInset?: boolean;
}): string | undefined {
  const classes = [
    opts?.wrapperInset ? COMPOSE_REACTIVE_SLOT_INSET_CLASS : undefined,
    opts?.wrapperClassName,
  ].filter(Boolean);
  return classes.length > 0 ? classes.join(' ') : undefined;
}

/**
 * Creates a stable-reference snapshot selector for `reactiveSlotBlock`.
 *
 * `ReactiveSlotInner` uses `useSyncExternalStore` — it re-renders whenever the
 * `getSnapshot` return value changes by reference. This factory returns a
 * `getSnapshot` function that performs shallow comparison on the extracted
 * fields and returns the **same object reference** when nothing changed,
 * preventing unnecessary re-renders.
 *
 * @param getContext - reads the current external state (e.g. session context singleton)
 * @param extract   - maps context → a flat snapshot object with the fields to track
 *
 * @example
 * ```ts
 * const getStableSlice = createStableSnapshot(
 *   getReleaseSessionContext,
 *   ctx => ({ submitted: !!ctx.submitted, modalStatus: ctx.modalStatus, count: ctx.numberOfInclude }),
 * );
 * reactiveSlotBlock(subscribe, getStableSlice, buildTabs, { wrapperInset: true });
 * ```
 */
export function createStableSnapshot<TCtx, TSlice extends Record<string, unknown>>(
  getContext: () => TCtx,
  extract: (ctx: TCtx) => TSlice,
): () => TSlice {
  let prev: TSlice = extract(getContext());

  return () => {
    const next = extract(getContext());
    const keys = Object.keys(next) as (keyof TSlice)[];
    const changed = keys.some((k) => next[k] !== prev[k]);
    if (!changed) return prev;
    prev = next;
    return next;
  };
}

export function reactiveSlotBlock<T>(
  subscribe: (listener: () => void) => () => void,
  getSnapshot: () => T,
  factory: (snapshot: T) => AnyBlock | null,
  opts?: {
    semanticId?: string;
    wrapperStyle?: Record<string, unknown>;
    wrapperClassName?: string;
    /** Responsive inset padding for tab/panel slots below a form (replaces fixed inline padding). */
    wrapperInset?: boolean;
    wire?: import('../types/blocks').SlotBlock['wire'];
  },
): SlotBlock {
  return slotBlock(
    (_shared) => createElement(ReactiveSlotInner, {
      subscribe,
      getSnapshot,
      factory,
      wrapperStyle: opts?.wrapperStyle,
      wrapperClassName: resolveReactiveSlotWrapperClassName(opts),
    } as any),
    { semanticId: opts?.semanticId, wire: opts?.wire },
  );
}
